import { FC, useRef, useState } from 'react';
import Slider from 'react-slick';
import { PlayFill, PauseFill } from '@geist-ui/icons';
import { Flex, Space, UnstyledButton } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { SlideItemSmall, SlideItemMedium } from './components';
import classNames from 'classnames';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import styles from './styles.module.scss';

interface SlideProps {
  title: string;
  description: string;
  image: string;
  altText: string;
}

interface Props {
  slides: SlideProps[];
  size?: 'small' | 'medium';
  isWidget?: boolean;
  className?: string;
}

const Carousel: FC<Props> = ({ slides, size = 'medium', isWidget = false, className }: Props): JSX.Element => {
  const [t] = useTranslation('carousel');
  const [isAutoplayStopped, setAutoplayStopped] = useState(false);
  const carousel = useRef<Slider | null>(null);
  const [slideIndex, setSlideIndex] = useState(1);
  const [slideChangeSource, setSlideChangeSource] = useState<'dot' | 'autoplay'>('autoplay');

  const imageSize: number = size === 'small' ? 160 : 400;

  const settings = {
    dots: true,
    accessible: true,
    infinite: true,
    speed: 700,
    autoplay: true,
    autoplaySpeed: 8000,
    slidesToShow: 1,
    slidesToScroll: 1,
    className: styles.slick__slides,
    pauseOnDotsHover: true,
    pauseOnFocus: true,
    pauseOnHover: true,
    dotsClass: styles.button__bar,
    beforeChange: (_current: number, next: number) => {
      setSlideIndex(next + 1);
    },
    afterChange: () => {
      if (slideChangeSource === 'dot') {
        const heading = document.querySelector(`.slick-slide.slick-current h1`) as HTMLElement;
        heading?.focus();
      }
      setSlideChangeSource('autoplay');
    },
    customPaging: (i: number) => (
      <button
        type="button"
        aria-label={`${t('animationSlideLabel')}  ${i + 1}`}
        onKeyUp={(e) => {
          if (e.code === 'Space' || e.code === 'Enter') {
            setSlideChangeSource('dot');
          }
        }}
        onMouseDown={() => setSlideChangeSource('dot')}
      />
    ),
  };

  const toggleAutoplay = (): void => {
    if (!carousel.current) return;
    if (isAutoplayStopped) {
      carousel.current.slickPlay();
      setAutoplayStopped(false);
    } else {
      carousel.current.slickPause();
      setAutoplayStopped(true);
    }
  };

  return (
    <div
      className={classNames(styles.wrapper, className, {
        [styles[size]]: size === 'small',
        [styles['widget-wrapper']]: isWidget,
      })}>
      <Flex>
        <UnstyledButton
          className={styles.carousel__autoplay_button}
          aria-label={isAutoplayStopped ? `${t('playAnimationLabel')}` : `${t('pauseAnimationLabel')}`}
          aria-pressed={isAutoplayStopped}
          onClick={toggleAutoplay}>
          {isAutoplayStopped ? <PlayFill /> : <PauseFill />}
        </UnstyledButton>
        <p aria-label={`${slideIndex} ${t('of')} ${slides.length}`}>
          {slideIndex} / {slides.length}
        </p>
      </Flex>
      <Space h="xl" />
      <Slider {...settings} ref={carousel} arrows={false} aria-live="polite">
        {slides.map((slide, index) => (
          <>
            {size === 'small' ? (
              <SlideItemSmall
                key={slide.title}
                title={slide.title}
                altText={slide.altText}
                imageSize={imageSize}
                image={slide.image}
                imgNumber={index + 1}
                description={slide.description}
              />
            ) : (
              <SlideItemMedium
                key={slide.title}
                title={slide.title}
                altText={slide.altText}
                imageSize={imageSize}
                image={slide.image}
                imgNumber={index + 1}
                description={slide.description}
                className={styles.slide}
              />
            )}
          </>
        ))}
      </Slider>
    </div>
  );
};

export default Carousel;
